import React, { useState } from 'react';
import { getElementTypeIcon, getElementTypeLabel } from '../utils/internalElementIcons';
import { getIcon } from '../utils/icons';
import { typography, surfaces, borders, textColors } from '../../design-system/tokens';

/**
 * InternalElementRow Component
 * 
 * Displays a single internal element with:
 * - SVG icon representing the element type
 * - Type label and element-specific label
 * - Edit and Delete action buttons (always visible)
 * - Loading state during delete operation
 * - Clickable row to edit element
 */
const InternalElementRow = ({ element, onEdit, onDelete, showDelete = true }) => {
  const [deleting, setDeleting] = useState(false);
  const iconName = getElementTypeIcon(element.type);
  const typeLabel = getElementTypeLabel(element.type);

  const handleDelete = async (e) => {
    // Stop propagation to prevent row click from triggering
    e.stopPropagation();
    
    setDeleting(true);
    try {
      await onDelete(element);
    } finally {
      // Note: component will likely unmount due to page reload, but this ensures cleanup if not
      setDeleting(false);
    }
  };

  const handleEdit = (e) => {
    // Stop propagation to prevent row click from triggering when clicking the button
    e.stopPropagation();
    onEdit(element);
  };

  const handleRowClick = () => {
    onEdit(element);
  };

  return (
    <div 
      className={`group flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer border ${borders.default} ${surfaces.primary} ${surfaces.hover}`}
      onClick={handleRowClick}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Icon */}
        <span className={`flex-shrink-0 ${textColors.tertiary}`}>
          {getIcon(iconName)}
        </span>
        
        {/* Labels */}
        <div className="min-w-0 flex-1">
          <div className={`${typography.overline} ${textColors.tertiary}`}>
            {typeLabel}
          </div>
          <div className={`${typography.bodySmall} ${textColors.primary} truncate`}>
            {element.label || 'Untitled'}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleEdit}
          className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Edit"
          aria-label={`Edit ${typeLabel}`}
          disabled={deleting}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        
        {showDelete && typeof onDelete === "function" && (
        <button
          onClick={handleDelete}
          className={`p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Delete"
          aria-label={`Delete ${typeLabel}`}
          disabled={deleting}
        >
          {deleting ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          )}
        </button>
        )}
      </div>
    </div>
  );
};

/**
 * InternalElementsList Component
 * 
 * Displays a list of internal elements (child items) within a container.
 * Shows an empty state when no elements exist.
 * 
 * @param {Array} elements - Array of internal elements with { id, type, label, order }
 * @param {Function} onEdit - Handler called when edit button is clicked
 * @param {Function} onDelete - Handler called when delete button is clicked
 */
export const InternalElementsList = ({ elements, onEdit, onDelete, showDelete = true }) => {
  if (!elements || elements.length === 0) {
    return (
      <div className={`mt-3 p-4 text-center ${typography.bodySmall} ${textColors.tertiary}`}>
        No internal elements
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4 pb-2">
      {elements.map(element => (
        <InternalElementRow 
          key={`${element.type}-${element.id}`}
          element={element}
          onEdit={onEdit}
          onDelete={onDelete}
          showDelete={showDelete}
        />
      ))}
    </div>
  );
};

export default InternalElementsList;

